import type { ReferenceList } from '@app-types/reference-list';

export const REFERENCE_LIST_KEYS = {
  ASSESSMENT_STATUS: 'assessment_status',
  MEETING_STATUS: 'meeting_status',
  EVIDENCE_STATUS: 'evidence_status',
  ASSESSMENT_RESULT: 'assessment_result',
  CONTRACT_STATUS: 'contract_status',
  SUPPLIER_STATUS: 'supplier_status',
  CONTACT_STATUS: 'contact_status',
  RESCHEDULE_REASON: 'reschedule_reason',
  CANCELLATION_REASON: 'cancellation_reason',
  PROCESS_TYPE: 'process_type',
  PROCESS_PRIORITY: 'process_priority',
  SCOPE: 'scope',
} as const;

export const QUERY_FIELD_LIST_KEY: Record<string, string> = {
  'supplier.status': REFERENCE_LIST_KEYS.SUPPLIER_STATUS,
  'contract.status': REFERENCE_LIST_KEYS.CONTRACT_STATUS,
  'contract.assessmentScope': REFERENCE_LIST_KEYS.SCOPE,
  'assessment.status': REFERENCE_LIST_KEYS.ASSESSMENT_STATUS,
  'meeting.status': REFERENCE_LIST_KEYS.MEETING_STATUS,
  'contact.status': REFERENCE_LIST_KEYS.CONTACT_STATUS,
};

const FALLBACK_MEETING_STATUS: Record<string, string> = {
  pending: 'Pending Scheduling',
  scheduled: 'Scheduled',
  rescheduled: 'Rescheduled',
  completed: 'Closed',
  cancelled: 'Cancelled',
  not_scheduled: 'Not Scheduled',
};

const FALLBACK_EVIDENCE_STATUS: Record<string, string> = {
  draft: 'Draft',
  pending: 'Under Review',
  approved: 'Approved',
  rejected: 'More Info Needed',
};

const FALLBACK_PROCESS_TYPE: Record<string, string> = {
  ENT: 'Enterprise process',
  OPS: 'Operational process',
};

export const REFERENCE_CATALOG_QUERY_KEY = ['reference-catalog'] as const;
export const SUPPLIER_TIERS_QUERY_KEY = ['suppliers', 'tiers'] as const;

type LabelMap = Map<string, Map<string, string>>;

let labels: LabelMap = new Map();
let tierNameToCode = new Map<string, string>();
let tierNames: string[] = [];

export interface CatalogTier {
  code: string;
  name: string;
}

export function setReferenceCatalog(lists: ReferenceList[], tiers: CatalogTier[] = []): void {
  const next: LabelMap = new Map();
  for (const list of lists) {
    const values = new Map<string, string>();
    for (const value of list.values) {
      values.set(value.code, value.label);
    }
    next.set(list.key, values);
  }
  labels = next;

  const names: string[] = [];
  const nameToCode = new Map<string, string>();
  for (const tier of tiers) {
    names.push(tier.name);
    nameToCode.set(tier.name, tier.code);
  }
  tierNames = names;
  tierNameToCode = nameToCode;
}

export function resolveLabel(listKey: string, code: string | null | undefined): string {
  if (!code) return '';
  return labels.get(listKey)?.get(code) ?? fallbackLabel(listKey, code);
}

export function listOptions(listKey: string): { value: string; label: string }[] {
  const list = labels.get(listKey);
  if (!list || list.size === 0) return [];
  return Array.from(list.entries()).map(([value, label]) => ({ value, label }));
}

export function getTierCodeForName(name: string | null | undefined): string | undefined {
  if (!name) return undefined;
  return tierNameToCode.get(name);
}

export function getCatalogTierNames(): string[] {
  return tierNames;
}

export function getMeetingStatusLabel(status: string): string {
  return resolveLabel(REFERENCE_LIST_KEYS.MEETING_STATUS, status.toLowerCase());
}

export function getEvidenceStatusLabel(status: string): string {
  return resolveLabel(REFERENCE_LIST_KEYS.EVIDENCE_STATUS, status);
}

export function getProcessTypeLabel(processType: string): string {
  return resolveLabel(REFERENCE_LIST_KEYS.PROCESS_TYPE, processType);
}

export function getAssessmentStatusLabel(status: string): string {
  return resolveLabel(REFERENCE_LIST_KEYS.ASSESSMENT_STATUS, status);
}

export function getContractStatusLabel(status: string): string {
  return resolveLabel(REFERENCE_LIST_KEYS.CONTRACT_STATUS, status);
}

export function getSupplierStatusLabel(status: string): string {
  return resolveLabel(REFERENCE_LIST_KEYS.SUPPLIER_STATUS, status);
}

export function getContactStatusLabel(status: string): string {
  return resolveLabel(REFERENCE_LIST_KEYS.CONTACT_STATUS, status);
}

export function getScopeLabel(scope: string | null | undefined): string {
  if (!scope) return '';
  return resolveLabel(REFERENCE_LIST_KEYS.SCOPE, toScopeCode(scope));
}

export function getQueryFieldLabel(entity: string, field: string, value: string): string {
  const listKey = QUERY_FIELD_LIST_KEY[`${entity}.${field}`];
  if (!listKey) return value;
  return resolveLabel(listKey, value);
}

function toScopeCode(scope: string): string {
  const normalized = scope.trim().toLowerCase();
  if (normalized === 'include' || normalized === 'included') {
    return 'Include';
  }
  if (normalized === 'exclude' || normalized === 'excluded') {
    return 'Exclude';
  }
  return scope;
}

function fallbackLabel(listKey: string, code: string): string {
  if (listKey === REFERENCE_LIST_KEYS.MEETING_STATUS) {
    return FALLBACK_MEETING_STATUS[code.toLowerCase()] ?? code;
  }
  if (listKey === REFERENCE_LIST_KEYS.EVIDENCE_STATUS) {
    return FALLBACK_EVIDENCE_STATUS[code] ?? code;
  }
  if (listKey === REFERENCE_LIST_KEYS.PROCESS_TYPE) {
    return FALLBACK_PROCESS_TYPE[code] ?? code;
  }
  return code;
}
